import SlideLayout from './SlideLayout';
import { formatKnowledgeTrustDate } from './utils';

function formatRelativeFreshness(timestamp) {
  if (!timestamp) return null;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 60) return { primary: `${Math.max(diffMinutes, 1)}m`, secondary: 'ago' };
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 36) return { primary: `${diffHours}h`, secondary: 'ago' };
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 14) return { primary: `${diffDays}d`, secondary: 'ago' };
  const absolute = formatKnowledgeTrustDate(timestamp);
  return absolute ? { primary: absolute, secondary: '' } : null;
}

export default function KnowledgeTrustBar({ knowledgeTrust, orgName }) {
  if (!knowledgeTrust) return null;

  const domain = knowledgeTrust.domain || null;
  const pageCount = Number.isFinite(Number(knowledgeTrust.page_count))
    ? Number(knowledgeTrust.page_count)
    : null;
  const freshness = formatRelativeFreshness(knowledgeTrust.last_refreshed_at);
  const topics = Array.isArray(knowledgeTrust.coverage_topics)
    ? knowledgeTrust.coverage_topics.filter(Boolean).slice(0, 6)
    : [];

  if (!domain && !pageCount && !freshness && topics.length === 0) {
    return null;
  }

  const title = domain
    ? `Already running live on ${domain}.`
    : `Already running live on ${orgName || 'your'} content.`;

  const stats = [
    {
      label: 'Source',
      primary: domain || 'Client website',
      secondary: 'public content',
    },
    {
      label: 'Indexed',
      primary: pageCount ? pageCount.toLocaleString() : '—',
      secondary: pageCount === 1 ? 'page' : 'pages',
    },
    {
      label: 'Refreshed',
      primary: freshness?.primary || 'Live',
      secondary: freshness ? freshness.secondary : 'knowledge base',
    },
  ];

  const flowSteps = [
    {
      label: 'Continuously refreshed',
      body: `Add or edit a page on ${domain || 'your site'} and it flows into answers on the next refresh — no manual sync, no separate content store.`,
    },
    {
      label: 'Cited back to the source',
      body: 'Every answer links to the page it came from, so a member can verify before acting and your staff has a clean audit trail.',
    },
    {
      label: 'Public scope only',
      body: 'We index your public website. CRM data, member records, and internal documents stay exactly where they are.',
    },
  ];

  return (
    <SlideLayout
      eyebrow="Live content coverage"
      title={title}
      intro="No integration. No IT project. No data leaves your organization."
    >
      <div className="flex h-full flex-col gap-10 sm:gap-12">
        <div className="space-y-6 sm:space-y-7">
          <div className="grid gap-px overflow-hidden border border-tf-stone bg-tf-stone sm:grid-cols-3">
            {stats.map((stat) => {
              const primaryStr = String(stat.primary || '');
              // Domain-style strings (no spaces, contains a dot) shrink to avoid clipping.
              const isLongUnbreakable = primaryStr.length > 12 && !/\s/.test(primaryStr);
              const primarySizeClass = isLongUnbreakable
                ? 'text-[22px] sm:text-[26px]'
                : 'text-[36px] sm:text-[42px]';
              return (
                <div
                  key={stat.label}
                  className="flex h-full min-w-0 flex-col justify-between gap-6 bg-tf-surface px-6 py-7 sm:px-7 sm:py-8"
                >
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-tf-ink/55">
                    {stat.label}
                  </p>
                  <p className="flex flex-wrap items-baseline gap-2">
                    <span
                      className={`break-all font-brand font-light leading-none tracking-[-0.01em] text-tf-ink ${primarySizeClass}`}
                    >
                      {stat.primary}
                    </span>
                    {stat.secondary ? (
                      <span className="text-[14px] font-light text-tf-ink/65">
                        {stat.secondary}
                      </span>
                    ) : null}
                  </p>
                </div>
              );
            })}
          </div>

          {topics.length > 0 && (
            <div className="space-y-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-tf-ink/55">
                Coverage
              </p>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => (
                  <span
                    key={topic}
                    className="inline-flex items-center border border-tf-stone bg-tf-surface px-3 py-1.5 text-[13px] font-light text-tf-ink"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-tf-stone pt-6 sm:pt-7">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-tf-ink/55">
            How this works
          </p>
          <dl className="mt-4 grid gap-x-8 gap-y-5 sm:mt-5 sm:grid-cols-3 sm:gap-y-6">
            {flowSteps.map((step) => (
              <div key={step.label} className="space-y-2">
                <dt className="text-[15px] font-medium text-tf-ink sm:text-[16px]">{step.label}</dt>
                <dd className="text-[14px] font-light leading-[1.55] text-tf-ink/75">
                  {step.body}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </SlideLayout>
  );
}
